import type { FetchResult } from '@apollo/client';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import type { AssessmentType } from 'generated/types';
import type {
  AdminUpdateUserNameMutation,
  AdminUpdateUserNameMutationVariables,
  PostUserImportJobMutation,
  PostUserImportJobMutationVariables,
  UpdateUserNameMutation,
  UpdateUserNameMutationVariables,
  UpdateUserVitallConsentMutation,
  UpdateUserVitallConsentMutationVariables,
} from 'generated/user.generated';
import {
  useAdminUpdateUserNameMutation,
  useGetAllNonPatientUsersQuery,
  useGetAllPatientUsersQuery,
  useGetAllUsersQuery,
  useGetUserMfaSettingsQuery,
  useGetUserRecordQuery,
  useListUserImportJobsQuery,
  usePostUserImportJobMutation,
  useUpdateUserNameMutation,
  useUpdateUserVitallConsentMutation,
} from 'generated/user.generated';

export const useGetAllUsers = () => {
  const response = useGetAllUsersQuery();
  return { ...response, data: response.data?.getAllUsers };
};

// Gets all users that are also vitall_patients
export const useGetAllPatientUsers = (params?: {
  user_id?: string;
  assessmentType?: AssessmentType;
}) => {
  const {
    state: { isCurator, isOrg, isPractitioner },
  } = useSession();
  const response = useGetAllPatientUsersQuery({
    variables: {
      input: {
        user_id: params?.user_id,
      },
      assessment_type: params?.assessmentType,
      is_vitall: isCurator,
      is_org: isOrg,
      isPractitioner: isPractitioner,
      includeUserPlan: isCurator || isPractitioner,
    },
  });
  return { ...response, data: response.data?.getAllPatientUsers };
};

export const useGetPatientUser = (params: { id: string }) => {
  const response = useGetAllPatientUsersQuery();
  const patient = response.data?.getAllPatientUsers.find(
    ({ vitall_patient }) => vitall_patient?.vitall_patient_id === params.id
  );
  return patient;
};

// Gets all users that are not vitall_patients
export const useGetAllNonPatientUsers = (opts?: { skip: boolean }) => {
  const {
    state: { isVitall },
  } = useSession();
  const response = useGetAllNonPatientUsersQuery({
    // Match ClientList tooltip gating (isVitall = Admin || Curator) so Admins
    // also receive account.parent_id for correct parent/child icons.
    variables: { is_vitall: isVitall },
    skip: opts?.skip,
  });
  return { ...response, data: response.data?.getAllNonPatientUsers };
};

export const useGetUser = (params?: { id: string }) => {
  const {
    state: { loggedInProfile },
  } = useSession();
  const user_id = params?.id ?? loggedInProfile?.user_id ?? '';
  const response = useGetUserRecordQuery({
    variables: { input: { user_id } },
    skip: !user_id,
  });
  return response;
};

export const useGetAllUsersWithinAccount = () => {
  const {
    state: { activeProfile },
  } = useSession();
  const user_id = activeProfile?.user_id ?? '';
  const response = useGetUserRecordQuery({
    variables: { input: { user_id } },
    skip: !user_id,
  });
  const { data } = response;
  const users = data?.getUserRecord.users ?? [];
  return { ...response, data: users };
};

export const useListUserImportJobs = (params?: {
  limit?: number;
  token?: string;
}) => {
  const response = useListUserImportJobsQuery({
    variables: {
      input: {
        limit: params?.limit ?? 10,
        token: params?.token ?? '',
      },
    },
  });
  const data = response.data?.listUserImportJobs;
  return { ...response, data };
};

export function useGetUserMfaSettings() {
  const {
    state: { activeProfile },
  } = useSession();
  const user_id = activeProfile?.user_id ?? '';
  const response = useGetUserMfaSettingsQuery({
    variables: { input: { user_id } },
    skip: !user_id,
  });

  return response;
}

export function useUpdateUserName(): [
  (
    params: UpdateUserNameMutationVariables
  ) => Promise<FetchResult<UpdateUserNameMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'changeName',
  });
  const [mutate, { loading: baseLoading }] = useUpdateUserNameMutation();
  const loading = baseLoading;

  async function updateUserName(params: UpdateUserNameMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.changeName.success && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [updateUserName, loading];
}

// Admin-scoped counterpart to useUpdateUserName — same shape, calls
// adminChangeName instead. See UpdateUserResolver.adminChangeName on the
// backend for why this is a separate mutation rather than a role check on
// changeName itself.
export function useAdminChangeName(): [
  (
    params: AdminUpdateUserNameMutationVariables
  ) => Promise<FetchResult<AdminUpdateUserNameMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'adminChangeName',
  });
  const [mutate, { loading: baseLoading }] = useAdminUpdateUserNameMutation();
  const loading = baseLoading;

  async function adminChangeName(params: AdminUpdateUserNameMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.adminChangeName.success && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [adminChangeName, loading];
}

export function useUpdateUserConsent(): [
  (
    params: UpdateUserVitallConsentMutationVariables
  ) => Promise<FetchResult<UpdateUserVitallConsentMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'upsertConsent',
  });
  const [mutate, { loading: baseLoading }] =
    useUpdateUserVitallConsentMutation();
  const loading = baseLoading;

  async function updateUser(params: UpdateUserVitallConsentMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.updateVitallConsent.data && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [updateUser, loading];
}

export function useCreateUserImportJob(): [
  (
    params: PostUserImportJobMutationVariables
  ) => Promise<FetchResult<PostUserImportJobMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = usePostUserImportJobMutation();
  const loading = baseLoading;

  async function createImportJob(params: PostUserImportJobMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    return response;
  }
  return [createImportJob, loading];
}
